'use client';

import ky from 'ky';
import { find } from 'lodash-es';
import { match } from 'path-to-regexp';

import type { ReactNode } from 'react';
import { Suspense, createContext, useContext, useEffect, useMemo, useRef } from 'react';

import type { ReadonlyURLSearchParams } from 'next/navigation';

import { useEffectEvent } from '@/hooks/useEventEffect';
import { useOnRouteChange } from '@/hooks/useOnRouteChange';
import { type AppConfig } from '@/types/AppConfig';
import type { SegmentEvent } from '@/types/SegmentEvent';
import { normalizePathname } from '@/utils/links';
import { waitFor } from '@/utils/wait';

export type AnalyticsButtonEventArgs = {
  action: string | string[];
  text?: string;
  milestoneName?: string;
};

const defaultPrimaryCategory = 'PC010';
const primaryCategoryByPathname = [
  { route: match('/quantum'), category: 'PC010' },
  { route: match('/quantum/blog{/*rest}'), category: 'PC090' },
  { route: match('/quantum/case-studies{/*rest}'), category: 'PC080' },
  { route: match('/quantum/feedback-program'), category: 'PC010' },
  { route: match('/quantum/products'), category: 'PC230' },
  { route: match('/quantum/qiskit{/*rest}'), category: 'PC020' },
  { route: match('/quantum/quantum-safe'), category: 'PC020' },
  { route: match('/quantum/research'), category: 'PC020' },
  { route: match('/quantum/hardware'), category: 'PC010' },
];

type AnalyticsContextType = {
  trackButtonEvent: (args: AnalyticsButtonEventArgs) => void;
  trackRoute: (pathname: string, searchParams: ReadonlyURLSearchParams) => void;
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  trackButtonEvent: () => {},
  trackRoute: () => {},
});

export function AnalyticsProvider(props: { children: ReactNode }): ReactNode {
  const { children } = props;
  const queue = useRef<SegmentEvent[]>([]);
  const segmentStatus = useRef<'not-initialized' | 'pending' | 'loaded' | 'error'>(
    'not-initialized'
  );

  function trackEvent(event: SegmentEvent) {
    if (typeof window !== 'undefined') {
      if (segmentStatus.current === 'pending') {
        queue.current.push(event);

        return;
      } else if (segmentStatus.current === 'loaded') {
        try {
          trackClientEvent(event);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          // ignore errors
        }
      }
    }
  }

  const trackRoute = useEffectEvent((pathname: string, searchParams: ReadonlyURLSearchParams) => {
    const normalizedPathname = normalizePathname(pathname);

    return trackEvent({
      type: 'Page',
      route: 'quantum',
      app: getCategoryForPathname(normalizedPathname),
      info: {
        navigationType: 'pushState',
        params: searchParams.toString(),
        path: normalizedPathname,
        version: '1.0.0',
      },
    });
  });

  const trackButtonEvent = useEffectEvent((event: AnalyticsButtonEventArgs) => {
    const { action, text, milestoneName } = event;
    const pathname = normalizePathname(window.location.pathname);

    return trackEvent({
      type: 'CTA Clicked',
      info: {
        category: getCategoryForPathname(pathname),
        CTA: action,
        location: pathname,
        text: text!,
        milestoneName,
        channel: 'webpage',
      },
    });
  });

  const providerValue = useMemo(() => {
    return { trackButtonEvent, trackRoute };
  }, [trackButtonEvent, trackRoute]);

  const initializeAnalyticsQueue = useEffectEvent(async () => {
    await waitFor(
      async () => {
        if (isSegmentLoaded()) return true;
        await new Promise((resolve) =>
          window.requestIdleCallback
            ? window.requestIdleCallback(resolve)
            : window.setTimeout(resolve)
        );

        return false;
      },
      { wait: 1000, timeout: 60 * 1000 }
    );

    if (isSegmentLoaded()) {
      segmentStatus.current = 'loaded';

      queue.current.forEach((event) => trackEvent(event));
      queue.current = [];
    } else {
      segmentStatus.current = 'error';
    }
  });

  useEffect(() => {
    async function init() {
      if (segmentStatus.current !== 'not-initialized') return;
      segmentStatus.current = 'pending';
      await initAnalytics();
      await initializeAnalyticsQueue();
    }

    init().catch((e) => {
      segmentStatus.current = 'error';

      // eslint-disable-next-line
      console.error('Error loading analytics', e);
    });
  }, [initializeAnalyticsQueue]);

  return (
    <AnalyticsContext.Provider value={providerValue}>
      {children}

      <Suspense>
        <RouteChangeTracker />
      </Suspense>
    </AnalyticsContext.Provider>
  );
}

async function initAnalytics() {
  try {
    const appConfig = await ky.get('/quantum/api/config').json<AppConfig>();

    if (!appConfig.analytics.segment) return;

    window._analytics = {
      segment_key: appConfig.analytics.segment.key,
      coremetrics: false,
      optimizely: false,
      googleAddServices: false,
      fullStory: false,
      autoPageEventSpa: false,
      autoFormEvents: false,
      autoPageView: false,
      // Only needed for local development (localhost)
      // In staging/production, ibm-common.js detects IBM domain and works fine
      ...(typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? { analyticsServiceUrl: 'https://test.cloud.ibm.com/analytics' }
        : {}),
    };
    window._ibmAnalytics = {
      settings: {
        name: 'Systems_Other',
      },
      onLoad: [['ibmStats.pageview', []]],
      trustarc: {
        privacyPolicyLink: 'https://www.ibm.com/us-en/privacy',
      },
    };

    window.digitalData = {
      page: {
        category: {
          primaryCategory: defaultPrimaryCategory,
        },
        pageInfo: {
          segment: {
            enabled: true,
          },
          productTitle: appConfig.analytics.segment.productTitle,
          analytics: {
            category: defaultPrimaryCategory,
          },
          ibm: {
            /* siteId: It suppose to be "IBM_" + _ibmAnalytics.settings.name */
            /* So if our siteID is IBM_Systems_Other, the name should be Systems_Other */
            siteID: appConfig.analytics.segment.siteId,
          },
        },
      },
    };

    await loadScript(appConfig.analytics.segment.script);
    await loadScript('//1.www.s81c.com/common/carbon/autotrack.min.js', initializeAutotrack);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // error loading analytics
  }
}

function initializeAutotrack() {
  if (!window.autoTrack) {
    // eslint-disable-next-line no-console
    console.error('Autotrack not initialized');

    return;
  }

  window.autoTrack.updateCallback(autotrackCallback);

  // Expose dev mode function for debugging
  window.autoTrackDev = function () {
    if (!window.autoTrack) return;

    window.autoTrack.setDevMode(true);
    const style = document.createElement('style');
    style.textContent = `
      .cds-autotrack-dev-highlight::after {
        content: attr(data-autotrack-cta);
      }
    `;
    document.head.appendChild(style);
  };
}

function autotrackCallback(eventType: 'UI Interaction', properties?: Record<string, any>) {
  const pathname = normalizePathname(window.location.pathname);
  const category = getCategoryForPathname(pathname);

  const enrichedProperties = {
    ...properties,
    category,
    channel: 'webpage',
  };

  window.bluemixAnalytics?.trackEvent(eventType, enrichedProperties);
}

function loadScript(src: string, onLoad?: () => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      onLoad?.();
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function RouteChangeTracker() {
  const { trackRoute } = useAnalyticsContext();
  useOnRouteChange(({ pathname, searchParams }) => {
    trackRoute(pathname, searchParams);
  });

  return null;
}

function getCategoryForPathname(pathname?: string): string {
  if (!pathname) return defaultPrimaryCategory;
  const match = find(primaryCategoryByPathname, ({ route }) => !!route(pathname));

  return match?.category ?? defaultPrimaryCategory;
}

function isSegmentLoaded() {
  if (typeof window === 'undefined') return false;

  return !!window?.bluemixAnalytics;
}

function trackClientEvent(event: SegmentEvent) {
  switch (event.type) {
    case 'Page': {
      window.bluemixAnalytics?.pageEvent(event.app, event.route, {
        ...event.info,
        productTitle: window.digitalData.page.pageInfo.productTitle,
      });

      return;
    }
    case 'CTA Clicked':
      {
        const ctas = Array.isArray(event.info.CTA) ? event.info.CTA : [event.info.CTA];

        for (const cta of ctas) {
          window.bluemixAnalytics?.trackEvent('CTA Clicked', {
            ...event.info,
            CTA: cta,
            productTitle: window.digitalData.page.pageInfo.productTitle,
          });
        }
      }
      break;
  }
}

export function useAnalyticsContext() {
  return useContext(AnalyticsContext);
}
